// Shared document download/view helper.
// Used everywhere a stored document is downloadable (request supporting docs,
// payment proofs, transfer notes, report CSVs). Centralised so behaviour
// stays identical across the app and any future fix lands in one place.
//
// Behaviour:
//   - Fetches /api/files/{path} as a blob via the authed axios instance.
//   - asDownload=true → triggers a real file download with the given filename.
//   - asDownload=false → opens the document in a new tab (object URL).
//   - On failure: shows a SPECIFIC sonner toast (404 / 401 / network) so the
//     user always sees what happened — never a silent fail.
//   - Drive fallback: if a doc carries a `drive_link` and the storage fetch
//     fails (typically 404 — the storage copy was pruned), opens Drive in a
//     new tab so the user can still get to the document.

import { api } from "./api";
import { toast } from "sonner";

/**
 * @param {object} args
 * @param {string} args.url       Stored URL (e.g. "/api/files/vow/support/.../abc.pdf")
 * @param {string} [args.filename] Display filename for downloads
 * @param {boolean} [args.asDownload]
 * @param {string} [args.driveLink] Optional Drive fallback link (webViewLink)
 */
export async function downloadDoc({ url, filename, asDownload = false, driveLink = null }) {
  if (!url && !driveLink) {
    toast.error("Document is missing — nothing to open. Please re-upload.");
    return;
  }
  // If only Drive link exists (no storage path), just open it.
  if (!url && driveLink) {
    window.open(driveLink, "_blank", "noopener,noreferrer");
    return;
  }
  const cleanUrl = String(url).replace(/^\/api/, "");
  try {
    const res = await api.get(cleanUrl, { responseType: "blob" });
    const blob = res.data;
    const objectUrl = URL.createObjectURL(blob);
    if (asDownload) {
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = filename || "document";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } else {
      const w = window.open(objectUrl, "_blank", "noopener,noreferrer");
      if (!w) {
        // Pop-up blocked — fall back to programmatic anchor click in same tab.
        const a = document.createElement("a");
        a.href = objectUrl;
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
    }
  } catch (e) {
    const status = e?.response?.status;
    // Specific, actionable error messages — and Drive fallback on 404.
    if (status === 404) {
      if (driveLink) {
        toast.message("Stored copy not found — opening Google Drive instead.");
        window.open(driveLink, "_blank", "noopener,noreferrer");
        return;
      }
      toast.error("Document file no longer exists in storage. Ask the uploader to re-attach.");
    } else if (status === 401 || status === 403) {
      toast.error("Your session expired or you don't have access. Refresh and try again.");
    } else if (e?.code === "ERR_NETWORK" || !status) {
      toast.error("Connection failed — check your network and try again.");
    } else {
      toast.error(`Could not open document (HTTP ${status}). Please try again.`);
    }
  }
}
